/**
 * Where member information lives and moves — ONE view (operator order
 * 2026-08-31: "simple... the data life cycle of the org").
 *
 * The department list IS the map. Each department is a five-line story told
 * in order — what arrives, what it is used for, where it lives and who may
 * see it, where it goes, how long it is kept — with the owner's name on it.
 * A missing line says so in plain words. Everything is answered and
 * confirmed inline. The only second view is the picture, behind one button.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { api, Card, Pill, STATE, SOURCE, fmtDay, Tip } from './api';

function SourceLine({ flow }) {
  const s = flow.source || {};
  if (flow.source_kind === 'document') {
    return (
      <div className="bg-amber-50/70 border-l-4 border-amber-300 rounded-r-lg px-3 py-2 text-[12.5px] text-slate-700 whitespace-pre-wrap break-words">
        “{s.quote}”
        <div className="text-[11px] text-slate-500 mt-1">{s.document_role === 'baa' ? 'BAA' : s.document_role === 'contract' ? 'Contract' : 'Document'} · {s.document_name}{s.page ? ` · page ${s.page}` : ''} · read into the FDR contract register</div>
      </div>
    );
  }
  if (flow.source_kind === 'registry') return <div className="text-[12.5px] text-slate-600">Registry record: {s.title || 'universe request'}{s.files ? ` · ${s.files} file${s.files === 1 ? '' : 's'} received` : ''}{s.last_submitted ? ` · last ${fmtDay(s.last_submitted)}` : ''}.</div>;
  if (flow.source_kind === 'person') return <div className="text-[12.5px] text-slate-600">Said by <span className="font-semibold">{s.person}</span>{s.role ? `, ${s.role}` : ''}{s.date ? `, on ${fmtDay(s.date)}` : ''}.</div>;
  if (flow.source_kind === 'template') return <div className="text-[12.5px] text-slate-600">{s.note || SOURCE.template}</div>;
  return <div className="text-[12.5px] text-slate-600">{s.note || SOURCE.officer}</div>;
}

// Defined once, outside the editor: a wrapper created inside the render is a
// new component type on every keystroke, which remounts the input and drops
// focus after one character (live-found 2026-08-31).
function L({ label, children }) {
  return <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>{children}</label>;
}

function FlowEditor({ flow, kinds, basisLabels, onSaved, onClose }) {
  const [f, setF] = useState({ what: flow.what || '', purpose: flow.purpose || '', basis: flow.basis || 'not_stated', who_may_see: flow.who_may_see || '', kept_for: flow.kept_for || '', safeguards: flow.safeguards || '', information: Array.isArray(flow.information) ? flow.information : [], leaves_us: flow.leaves_us, review_note: flow.review_note || '' });
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleKind = (k) => set('information', f.information.includes(k) ? f.information.filter((x) => x !== k) : [...f.information, k]);
  const send = async (action) => {
    setBusy(action); setErr('');
    const d = await api(`/map/flows/${flow.id}`, { method: 'PATCH', body: { ...f, action } });
    setBusy('');
    if (!d.success) { setErr(d.message || 'Could not save'); return; }
    onSaved(d.flow);
  };
  const inp = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800';
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <L label="What moves"><input className={inp} value={f.what} onChange={(e) => set('what', e.target.value)} /></L>
        <L label="Why"><input className={inp} value={f.purpose} onChange={(e) => set('purpose', e.target.value)} placeholder="The work this information is for" /></L>
        <L label="Allowed because">
          <select className={inp} value={f.basis} onChange={(e) => set('basis', e.target.value)}>{Object.entries(basisLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        </L>
        <L label="Who may see it"><input className={inp} value={f.who_may_see} onChange={(e) => set('who_may_see', e.target.value)} placeholder="Team, roles, or the vendor" /></L>
        <L label="How long it is kept"><input className={inp} value={f.kept_for} onChange={(e) => set('kept_for', e.target.value)} placeholder="For example: 10 years, or returned at contract end" /></L>
        <L label="How it is protected"><input className={inp} value={f.safeguards} onChange={(e) => set('safeguards', e.target.value)} placeholder="Encrypted transfer, secure portal, locked mail" /></L>
      </div>
      <div>
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Kinds of member information</span>
        <div className="flex flex-wrap gap-1.5">
          {kinds.map((k) => (
            <button key={k} type="button" onClick={() => toggleKind(k)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${f.information.includes(k) ? 'bg-orange-500 text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-100'}`}>{k}</button>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Leaves the United States?</span>
        {[['no', false], ['yes', true], ['not sure', null]].map(([l, v]) => (
          <button key={l} type="button" onClick={() => set('leaves_us', v)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${f.leaves_us === v ? 'bg-slate-800 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>{l}</button>
        ))}
      </div>
      <L label="Note (optional)"><input className={inp} value={f.review_note} onChange={(e) => set('review_note', e.target.value)} placeholder="Anything the next reader should know" /></L>
      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={!!busy} onClick={() => send('confirm')} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold shadow-sm disabled:opacity-50">{busy === 'confirm' ? 'Saving…' : 'Confirm this flow'}</button>
        <button type="button" disabled={!!busy} onClick={() => send('save')} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm font-semibold disabled:opacity-50">Save a draft</button>
        <button type="button" disabled={!!busy} onClick={() => send('reject')} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-600 text-sm font-semibold disabled:opacity-50">It does not exist</button>
        <button type="button" onClick={onClose} className="ml-auto text-sm text-slate-500">Close</button>
      </div>
    </div>
  );
}

function DescribeFlow({ nodes, kinds, basisLabels, onCreated }) {
  const [open, setOpen] = useState(false);
  const blank = { from_node_id: '', what: '', purpose: '', basis: 'not_stated', who_may_see: '', kept_for: '', information: [], said_by: '', said_role: '' };
  const [f, setF] = useState(blank);
  const [tos, setTos] = useState([]); const [newOnes, setNewOnes] = useState([]);
  const [err, setErr] = useState(''); const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800';
  const submit = async () => {
    if (!f.from_node_id || (!tos.length && !newOnes.length) || !f.what.trim()) { setErr('Say where it comes from, where it goes, and what moves.'); return; }
    setBusy(true); setErr('');
    let toIds;
    try { toIds = [...tos, ...(await createNamedParties(newOnes))]; }
    catch (e) { setBusy(false); setErr(e.message); return; }
    const d = await api('/map/flows', { method: 'POST', body: { ...f, to_node_ids: toIds } });
    setBusy(false);
    if (!d.success) { setErr(d.message || 'Could not save'); return; }
    setOpen(false); setF(blank); setTos([]); setNewOnes([]);
    onCreated();
  };
  if (!open) return <button type="button" onClick={() => setOpen(true)} className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold shadow-sm">+ Describe a movement</button>;
  const opts = nodes.map((n) => <option key={n.id} value={n.id}>{n.kind === 'vendor' ? 'Vendor · ' : n.kind === 'external' ? '' : n.kind === 'system' ? 'System · ' : 'Department · '}{n.name}</option>);
  return (
    <Card title="Describe a movement" className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">From</span><select className={inp} value={f.from_node_id} onChange={(e) => set('from_node_id', e.target.value)}><option value="">Choose…</option>{opts}</select></label>
        <label className="block md:col-span-2"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">What moves</span><input className={inp} value={f.what} onChange={(e) => set('what', e.target.value)} placeholder="For example: monthly eligibility file" /></label>
        <PartyPicker label="To (one or several)" nodes={nodes.filter((n) => n.id !== f.from_node_id)} chosen={tos} setChosen={setTos}
          newOnes={newOnes} setNewOnes={setNewOnes} newLabel="Who or what receives it" inp={inp} />
        <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Why</span><input className={inp} value={f.purpose} onChange={(e) => set('purpose', e.target.value)} /></label>
        <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Allowed because</span><select className={inp} value={f.basis} onChange={(e) => set('basis', e.target.value)}>{Object.entries(basisLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
        <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Who may see it</span><input className={inp} value={f.who_may_see} onChange={(e) => set('who_may_see', e.target.value)} /></label>
        <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">How long it is kept</span><input className={inp} value={f.kept_for} onChange={(e) => set('kept_for', e.target.value)} /></label>
        <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Who told you (leave empty if you know it yourself)</span><input className={inp} value={f.said_by} onChange={(e) => set('said_by', e.target.value)} placeholder="Name" /></label>
        <label className="block"><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Their role</span><input className={inp} value={f.said_role} onChange={(e) => set('said_role', e.target.value)} placeholder="For example: Enrollment lead" /></label>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {kinds.map((k) => (
          <button key={k} type="button" onClick={() => set('information', f.information.includes(k) ? f.information.filter((x) => x !== k) : [...f.information, k])} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${f.information.includes(k) ? 'bg-orange-500 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>{k}</button>
        ))}
      </div>
      {err && <div className="text-xs text-red-600 mt-2">{err}</div>}
      <div className="mt-3 flex gap-2">
        <button type="button" disabled={busy} onClick={submit} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold disabled:opacity-50">{busy ? 'Saving…' : (f.said_by ? 'Add as said by this person' : 'Add as confirmed')}</button>
        <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-600 text-sm font-semibold">Cancel</button>
      </div>
    </Card>
  );
}

function HoldingEditor({ holding, kinds, onSaved }) {
  const [f, setF] = useState({ information: Array.isArray(holding.information) ? holding.information : [], systems: holding.systems || '', who_may_see: holding.who_may_see || '', kept_for: holding.kept_for || '', safeguards: holding.safeguards || '', leaves_us: holding.leaves_us, review_note: holding.review_note || '' });
  const [busy, setBusy] = useState(''); const [err, setErr] = useState('');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const send = async (action) => {
    setBusy(action); setErr('');
    const d = await api(`/map/holdings/${holding.id}`, { method: 'PATCH', body: { ...f, action } });
    setBusy('');
    if (!d.success) { setErr(d.message || 'Could not save'); return; }
    onSaved(d.holding);
  };
  const inp = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800';
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <div>
        <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">What member information this department holds</span>
        <div className="flex flex-wrap gap-1.5">
          {kinds.map((k) => (
            <button key={k} type="button" onClick={() => set('information', f.information.includes(k) ? f.information.filter((x) => x !== k) : [...f.information, k])} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${f.information.includes(k) ? 'bg-orange-500 text-white' : 'bg-white border border-slate-300 text-slate-600 hover:bg-slate-100'}`}>{k}</button>
          ))}
        </div>
        <p className="text-[11px] text-slate-400 mt-1">Leave every kind unpressed and confirm if this department holds no member information.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <L label="In which systems"><input className={inp} value={f.systems} onChange={(e) => set('systems', e.target.value)} placeholder="The systems, drives or files where it lives" /></L>
        <L label="Who in the department may see it"><input className={inp} value={f.who_may_see} onChange={(e) => set('who_may_see', e.target.value)} placeholder="Roles, and whether everyone sees everything" /></L>
        <L label="How long it is kept"><input className={inp} value={f.kept_for} onChange={(e) => set('kept_for', e.target.value)} placeholder="Medicare Advantage records: 10 years" /></L>
        <L label="How it is protected"><input className={inp} value={f.safeguards} onChange={(e) => set('safeguards', e.target.value)} placeholder="Access controls, encryption, locked storage" /></L>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Leaves the United States?</span>
        {[['no', false], ['yes', true], ['not sure', null]].map(([l, v]) => (
          <button key={l} type="button" onClick={() => set('leaves_us', v)} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${f.leaves_us === v ? 'bg-slate-800 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>{l}</button>
        ))}
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" disabled={!!busy} onClick={() => send('confirm')} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold shadow-sm disabled:opacity-50">{busy === 'confirm' ? 'Saving…' : 'Confirm this story'}</button>
        <button type="button" disabled={!!busy} onClick={() => send('save')} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm font-semibold disabled:opacity-50">Save without confirming</button>
        <span className="text-[10px] text-slate-400 ml-auto">Access follows the minimum necessary rule (45 CFR 164.514(d)) · retention 42 CFR 422.504(d)</span>
      </div>
    </div>
  );
}

/**
 * Answer an unanswered line RIGHT ON the line (operator order 2026-09-04:
 * "3 of 5 answered" with no way to answer the rest is not a program).
 * Line 1 and line 4 record a movement with this department preset; only the
 * essentials are asked. Line 2 gives every recorded movement its "why".
 */
/**
 * The parties on the other side of a movement: several at once (operator
 * order 2026-09-10: a department sends to its vendor, to CMS and to others in
 * one breath). Chosen parties show as chips; a party not on the map yet is
 * named right here and added to the list. Used by both entry forms.
 */
function PartyPicker({ label, nodes, chosen, setChosen, newOnes, setNewOnes, newLabel, inp }) {
  const [pick, setPick] = useState('');
  const [naming, setNaming] = useState(false);
  const [newName, setNewName] = useState(''); const [newKind, setNewKind] = useState('vendor');
  const kindLabel = (n) => (n.kind === 'vendor' ? 'Vendor · ' : n.kind === 'system' ? 'System · ' : n.kind === 'department' ? 'Department · ' : '');
  const addNew = () => {
    const name = newName.trim();
    if (name.length < 2) return;
    if (!newOnes.some((x) => x.name.toLowerCase() === name.toLowerCase())) setNewOnes([...newOnes, { name, kind: newKind }]);
    setNewName(''); setNaming(false);
  };
  return (
    <div className="md:col-span-2">
      <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{label}</span>
      {(chosen.length > 0 || newOnes.length > 0) && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {chosen.map((id) => { const n = nodes.find((x) => x.id === id); return (
            <span key={id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-800 text-white">
              {n ? `${kindLabel(n)}${n.name}` : 'Party'}
              <button type="button" onClick={() => setChosen(chosen.filter((x) => x !== id))} className="opacity-70 hover:opacity-100">✕</button>
            </span>); })}
          {newOnes.map((x) => (
            <span key={x.name} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-orange-500 text-white" title="Will be added to the map">
              {x.kind === 'vendor' ? 'Vendor · ' : x.kind === 'system' ? 'System · ' : ''}{x.name} <span className="opacity-80 font-normal">(new)</span>
              <button type="button" onClick={() => setNewOnes(newOnes.filter((y) => y.name !== x.name))} className="opacity-70 hover:opacity-100">✕</button>
            </span>
          ))}
        </div>
      )}
      <select className={inp} value={pick} onChange={(e) => {
        const v = e.target.value; setPick('');
        if (v === '__new__') { setNaming(true); return; }
        if (v && !chosen.includes(v)) setChosen([...chosen, v]);
      }}>
        <option value="">{chosen.length || newOnes.length ? 'Add another…' : 'Choose…'}</option>
        {nodes.filter((n) => !chosen.includes(n.id)).map((n) => <option key={n.id} value={n.id}>{kindLabel(n)}{n.name}</option>)}
        <option value="__new__">+ Not listed — name it…</option>
      </select>
      {naming && (
        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
          <L label={newLabel}>
            <input className={inp} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="For example: MD Flow" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addNew(); } }} />
          </L>
          <div>
            <span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">It is a…</span>
            <div className="flex flex-wrap gap-1.5 items-center">
              {[['vendor', 'Vendor'], ['system', 'System'], ['external', 'Someone else']].map(([k, l]) => (
                <button key={k} type="button" onClick={() => setNewKind(k)} className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold ${newKind === k ? 'bg-slate-800 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>{l}</button>
              ))}
              <button type="button" onClick={addNew} disabled={newName.trim().length < 2} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-orange-500 text-white disabled:opacity-50">Add to the list</button>
              <button type="button" onClick={() => { setNaming(false); setNewName(''); }} className="text-xs text-slate-500">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Create the parties named by hand; returns their ids or throws the message. */
async function createNamedParties(newOnes) {
  const ids = [];
  for (const x of newOnes) {
    const created = await api('/map/nodes', { method: 'POST', body: { name: x.name, kind: x.kind } });
    if (!created.success) throw new Error(created.message || 'Could not add it to the map.');
    ids.push(created.node.id);
  }
  return ids;
}

function QuickFlowAnswer({ dir, holding, nodes, kinds, onCreated, onClose }) {
  const [f, setF] = useState({ what: '', purpose: '', information: [] });
  const [chosen, setChosen] = useState([]); const [newOnes, setNewOnes] = useState([]);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const set = (k, v) => setF((p2) => ({ ...p2, [k]: v }));
  const inp = 'w-full border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800';
  const others = nodes.filter((n) => n.id !== holding.node_id);
  const submit = async () => {
    if ((!chosen.length && !newOnes.length) || !f.what.trim()) {
      setErr(dir === 'in' ? 'Say what arrives and where it comes from.' : 'Say what leaves and where it goes.'); return;
    }
    setBusy(true); setErr('');
    let otherIds;
    try { otherIds = [...chosen, ...(await createNamedParties(newOnes))]; }
    catch (e) { setBusy(false); setErr(e.message); return; }
    // One line per party: the department stays on its side, every party on the other.
    const body = {
      ...(dir === 'in' ? { from_node_ids: otherIds, to_node_id: holding.node_id } : { from_node_id: holding.node_id, to_node_ids: otherIds }),
      what: f.what, purpose: f.purpose, basis: 'not_stated',
      information: f.information, who_may_see: '', kept_for: '', said_by: '', said_role: '',
    };
    const d = await api('/map/flows', { method: 'POST', body });
    setBusy(false);
    if (!d.success) { setErr(d.message || 'Could not save'); return; }
    onCreated(); onClose();
  };
  return (
    <div className="mt-2 bg-amber-50/60 border border-amber-200 rounded-xl p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <L label={dir === 'in' ? 'What arrives' : 'What leaves'}>
          <input className={inp} value={f.what} onChange={(e) => set('what', e.target.value)} placeholder={dir === 'in' ? 'For example: monthly eligibility file' : 'For example: claims extract'} />
        </L>
        <L label="What the work is (why)"><input className={inp} value={f.purpose} onChange={(e) => set('purpose', e.target.value)} placeholder="The work this information is for" /></L>
        <PartyPicker label={dir === 'in' ? 'From (one or several)' : 'To (one or several)'} nodes={others} chosen={chosen} setChosen={setChosen}
          newOnes={newOnes} setNewOnes={setNewOnes} newLabel={dir === 'in' ? 'Who or what sends it' : 'Who or what receives it'} inp={inp} />
      </div>
      <div className="flex flex-wrap gap-1.5">
        {kinds.map((k) => (
          <button key={k} type="button" onClick={() => set('information', f.information.includes(k) ? f.information.filter((x) => x !== k) : [...f.information, k])} className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${f.information.includes(k) ? 'bg-orange-500 text-white' : 'bg-white border border-slate-300 text-slate-600'}`}>{k}</button>
        ))}
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="flex gap-2">
        <button type="button" disabled={busy} onClick={submit} className="px-4 py-2 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white text-sm font-semibold disabled:opacity-50">{busy ? 'Saving…' : (chosen.length + newOnes.length > 1 ? `Add ${chosen.length + newOnes.length} lines to the story` : 'Add it to the story')}</button>
        <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-300 bg-white text-slate-600 text-sm font-semibold">Cancel</button>
      </div>
    </div>
  );
}

function PurposeAnswer({ flows, onSaved, onClose }) {
  const missing = flows.filter((f) => !f.purpose);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(''); const [err, setErr] = useState('');
  const inp = 'flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800';
  const save = async (f) => {
    const purpose = (vals[f.id] || '').trim();
    if (!purpose) return;
    setBusy(f.id); setErr('');
    const d = await api(`/map/flows/${f.id}`, { method: 'PATCH', body: {
      what: f.what, purpose, basis: f.basis || 'not_stated', who_may_see: f.who_may_see || '',
      kept_for: f.kept_for || '', safeguards: f.safeguards || '',
      information: Array.isArray(f.information) ? f.information : [],
      leaves_us: f.leaves_us, review_note: f.review_note || '', action: 'save',
    } });
    setBusy('');
    if (!d.success) { setErr(d.message || 'Could not save'); return; }
    onSaved(d.flow);
  };
  if (!missing.length) return null;
  return (
    <div className="mt-2 bg-amber-50/60 border border-amber-200 rounded-xl p-4 space-y-2">
      <p className="text-xs text-slate-600">Say what each movement is for, in your own words:</p>
      {missing.map((f) => (
        <div key={f.id} className="flex items-center gap-2">
          <span className="text-[12px] text-slate-600 w-56 truncate flex-shrink-0">{f.what} · {f.to_name ? `to ${f.to_name}` : `from ${f.from_name}`}</span>
          <input className={inp} value={vals[f.id] || ''} onChange={(e) => setVals((v) => ({ ...v, [f.id]: e.target.value }))} placeholder="The work this is for" />
          <button type="button" disabled={busy === f.id} onClick={() => save(f)} className="px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold disabled:opacity-50">{busy === f.id ? 'Saving…' : 'Save'}</button>
        </div>
      ))}
      {err && <div className="text-xs text-red-600">{err}</div>}
      <button type="button" onClick={onClose} className="text-xs text-slate-500">Close</button>
    </div>
  );
}

/** The five-line story, always in lifecycle order. */
function storyOf(h, into, outof) {
  const kinds = Array.isArray(h.information) ? h.information : [];
  const purposes = [...new Set([...into, ...outof].map((f) => f.purpose).filter(Boolean))];
  // Every question names its subject — member information — and carries a
  // plain explanation with an example (operator order 2026-09-04: "where
  // what goes?" is not a question anyone can answer).
  return [
    { n: 1, label: 'What member information arrives here',
      tip: 'The files, records, or requests containing member information that REACH this department, and who sends each one. Example: authorization requests from providers; health risk assessments from members.',
      ok: into.length > 0, text: into.map((f) => `${f.what} from ${f.from_name}`).join(' · ') || null,
      ask: 'what member information reaches this department, and who sends it' },
    { n: 2, label: 'What this department uses it for',
      tip: 'The work that member information supports, in plain words. Example: reviewing authorization requests; building and updating care plans.',
      ok: purposes.length > 0, text: purposes.join(' · ') || null,
      ask: 'the work the member information is for' },
    { n: 3, label: 'Where the information is kept, and who may open it',
      tip: 'The systems, drives, or files where this department keeps member information, and the roles allowed to open them. Example: kept in the care plan system; opened by care managers only.',
      ok: !!(h.systems || h.who_may_see || kinds.length), text: [kinds.length ? kinds.join(', ') : null, h.systems ? `lives in ${h.systems}` : null, h.who_may_see ? `seen by ${h.who_may_see}` : null].filter(Boolean).join(' · ') || null,
      ask: 'the systems the member information lives in, and the roles who may open them' },
    { n: 4, label: 'Where this department sends it next',
      tip: 'Everyone this department sends member information TO — other departments, vendors, CMS, or the member. Example: care plans to the care plan vendor; universes to CMS.',
      ok: outof.length > 0, text: outof.map((f) => `${f.what} to ${f.to_name}`).join(' · ') || null,
      ask: 'who this department sends member information to: departments, vendors, CMS, members' },
    { n: 5, label: 'How long it is kept, and how it ends',
      tip: 'The retention period for the member information this department holds, and what happens when it ends. Medicare Advantage records: 10 years, then destroyed or returned.',
      ok: !!h.kept_for, text: [h.kept_for, h.leaves_us === true ? 'some of it leaves the United States' : h.leaves_us === false ? 'none of it leaves the United States' : null].filter(Boolean).join(' · ') || null,
      ask: 'how long it is kept — ten years for Medicare Advantage records — then destroyed or returned' },
  ];
}

const TWELVE_MONTHS = 365 * 24 * 60 * 60 * 1000;
const recent = (l) => { const d = l.discovery_date || l.breach_date; return d ? (Date.now() - new Date(d).getTime()) <= TWELVE_MONTHS : true; };
const INCIDENT_STATUS = {
  draft: 'Draft', under_investigation: 'Under investigation', risk_assessment_complete: 'Risk assessment complete',
  determination_pending_approval: 'Pending approval', approved_not_breach: 'Not a breach', approved_breach: 'Breach',
  notification_complete: 'Notification complete', closed: 'Closed',
};
const DETERMINATION = { not_violation: 'not a violation', violation_not_breach: 'violation, not a breach', breach_requiring_notification: 'breach, notification required', pending: 'determination pending' };

/**
 * Impermissible uses and breaches that came through this line (operator order
 * 2026-09-10): what broke, read from the incident module and never written
 * to it. The officer links a case to the line it came through; each case
 * opens in the incident module.
 */
function IncidentsOnLine({ flow, links, openIncident, onChanged }) {
  const [picking, setPicking] = useState(false);
  const [cases, setCases] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const mine = links.filter((l) => l.flow_id === flow.id);
  const loadCases = async () => { if (cases) return; const d = await api('/map/incidents'); if (d.success) setCases(d.incidents || []); };
  const link = async () => {
    if (!pick) return;
    setBusy(true); setErr('');
    const d = await api('/map/incident-links', { method: 'POST', body: { incident_id: pick, flow_id: flow.id } });
    setBusy(false);
    if (!d.success) { setErr(d.message || 'Could not record it'); return; }
    setPick(''); setPicking(false); onChanged();
  };
  const unlink = async (id) => { const d = await api(`/map/incident-links/${id}`, { method: 'DELETE' }); if (d.success) onChanged(); };
  return (
    <div className={`rounded-xl border p-3 ${mine.length ? 'bg-red-50/60 border-red-200' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${mine.length ? 'text-red-700' : 'text-slate-500'}`}>Impermissible uses and breaches that came through this line{mine.length ? ` (${mine.length})` : ''}</span>
        <button type="button" onClick={() => { setPicking(!picking); loadCases(); }} className="ml-auto text-xs font-semibold text-slate-600 hover:text-slate-900">{picking ? 'Cancel' : '+ A case came through here'}</button>
      </div>
      {mine.length > 0 && (
        <ul className="mt-2 space-y-1">
          {mine.map((l) => (
            <li key={l.id} className="flex items-center gap-2 text-sm">
              <span className="text-red-800 font-medium truncate">{l.title || 'Case'}</span>
              <span className="text-xs text-slate-500 flex-shrink-0">{l.discovery_date ? fmtDay(l.discovery_date) : ''}{l.determination && l.determination !== 'pending' ? ` · ${DETERMINATION[l.determination] || l.determination}` : ` · ${INCIDENT_STATUS[l.status] || l.status || ''}`}</span>
              <button type="button" onClick={() => openIncident?.(l.incident_id)} className="text-xs font-semibold text-orange-700 hover:underline flex-shrink-0">Open the case</button>
              {l.source === 'officer' && <button type="button" onClick={() => unlink(l.id)} className="text-xs text-slate-400 hover:text-slate-700 flex-shrink-0" title="This case did not come through this line">Not this line</button>}
            </li>
          ))}
        </ul>
      )}
      {picking && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <select value={pick} onChange={(e) => setPick(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-800 min-w-[280px]">
            <option value="">{cases === null ? 'Loading the cases…' : cases.length ? 'Which case?' : 'No cases on the record'}</option>
            {(cases || []).filter((c) => !mine.some((l) => l.incident_id === c.id)).map((c) => <option key={c.id} value={c.id}>{c.discovery_date ? `${fmtDay(c.discovery_date)} · ` : ''}{c.title || 'Case'}{c.department_name ? ` · ${c.department_name}` : ''}</option>)}
          </select>
          <button type="button" disabled={!pick || busy} onClick={link} className="px-3 py-2 rounded-lg bg-slate-800 text-white text-xs font-semibold disabled:opacity-50">{busy ? 'Saving…' : 'It came through this line'}</button>
          {err && <span className="text-xs text-red-600">{err}</span>}
        </div>
      )}
    </div>
  );
}

function DeptStory({ h, into, outof, kinds, basisLabels, nodes, open, onToggle, openFlowId, setOpenFlowId, onSaved, onHoldingSaved, reload, links = [], openIncident }) {
  const story = storyOf(h, into, outof);
  const answered = story.filter((s) => s.ok).length;
  // Cases in the last twelve months that came through this department: its
  // own node, or any line into or out of it.
  const lineIds = new Set([...into, ...outof].map((f) => f.id));
  const deptCases = new Set(links.filter((l) => recent(l) && (l.node_id === h.node_id || (l.flow_id && lineIds.has(l.flow_id)))).map((l) => l.incident_id));
  const [answering, setAnswering] = useState(null); // 1 | 2 | 4 | 'holding' | null
  const answerControl = (n) => {
    // Which control answers this line, in place.
    if (n === 1) return answering === 1
      ? <QuickFlowAnswer dir="in" holding={h} nodes={nodes} kinds={kinds} onCreated={reload} onClose={() => setAnswering(null)} />
      : null;
    if (n === 4) return answering === 4
      ? <QuickFlowAnswer dir="out" holding={h} nodes={nodes} kinds={kinds} onCreated={reload} onClose={() => setAnswering(null)} />
      : null;
    if (n === 2) {
      if (answering !== 2) return null;
      const flows = [...into, ...outof];
      return flows.length
        ? <PurposeAnswer flows={flows} onSaved={(f) => { onSaved(f); }} onClose={() => setAnswering(null)} />
        : <QuickFlowAnswer dir="in" holding={h} nodes={nodes} kinds={kinds} onCreated={reload} onClose={() => setAnswering(null)} />;
    }
    return null;
  };
  const FlowRow = ({ f, dir }) => {
    const n = links.filter((l) => l.flow_id === f.id && recent(l)).length;
    return (
    <div>
      <button type="button" onClick={() => setOpenFlowId(openFlowId === f.id ? null : f.id)} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 border border-slate-100">
        <span className="text-xs text-slate-500 flex-shrink-0">{dir === 'in' ? '←' : '→'}</span>
        <span className="text-[12.5px] text-slate-700 flex-1 min-w-0 truncate">{f.what} <span className="text-slate-400">· {dir === 'in' ? `from ${f.from_name}` : `to ${f.to_name}`}</span></span>
        {n > 0 && <span className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-red-100 text-red-800 flex-shrink-0" title="Impermissible uses or breaches in the last twelve months that came through this line">{n} {n === 1 ? 'case' : 'cases'}</span>}
        <Pill cfg={STATE[f.state]?.cls}>{STATE[f.state]?.label}</Pill>
      </button>
      {openFlowId === f.id && (
        <div className="mt-2 space-y-2">
          <SourceLine flow={f} />
          <FlowEditor flow={f} kinds={kinds} basisLabels={basisLabels} onSaved={onSaved} onClose={() => setOpenFlowId(null)} />
          <IncidentsOnLine flow={f} links={links} openIncident={openIncident} onChanged={reload} />
        </div>
      )}
    </div>
  ); };
  return (
    <div className="bg-white rounded-2xl border border-slate-200">
      <button type="button" onClick={onToggle} className="w-full text-left px-5 py-4 flex items-center gap-3 flex-wrap">
        <span className="font-semibold text-slate-800">{h.department_name}</span>
        {h.owners && <span className="text-xs text-slate-500">owned by {h.owners}</span>}
        <Pill cfg={STATE[h.state]?.cls}>{h.state === 'confirmed' ? 'Confirmed' : 'Waiting for you'}</Pill>
        {deptCases.size > 0 && <span className="px-2 py-0.5 rounded-full text-[10.5px] font-semibold bg-red-100 text-red-800" title="Impermissible uses or breaches in the last twelve months that came through this department">{deptCases.size} {deptCases.size === 1 ? 'impermissible use' : 'impermissible uses'} this year</span>}
        <span className="ml-auto text-xs text-slate-400 flex-shrink-0">{answered} of 5 answered</span>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          {h.state === 'confirmed' && <div className="text-xs text-emerald-700">Confirmed{h.confirmed_by_name ? ` by ${h.confirmed_by_name}` : ''}{h.confirmed_at ? ` on ${fmtDay(h.confirmed_at)}` : ''}.</div>}
          <ol className="space-y-2.5">
            {story.map((s) => (
              <li key={s.n} className="flex items-start gap-3">
                <span className={`mt-0.5 w-5 h-5 rounded-full flex-shrink-0 text-[11px] font-bold flex items-center justify-center ${s.ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{s.n}</span>
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    {s.label}
                    <Tip text={s.tip} />
                  </div>
                  {s.ok ? <div className="text-sm text-slate-800">{s.text}</div> : (
                    <div className="text-sm text-amber-700">
                      <span className="block mb-1.5">Not answered yet — {s.ask}.</span>
                      <button type="button"
                        onClick={() => setAnswering(s.n === 3 || s.n === 5 ? 'holding' : (answering === s.n ? null : s.n))}
                        className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold shadow-sm">
                        {s.n === 3 || s.n === 5 ? 'Answer it below ↓' : (answering === s.n ? 'Close' : 'Answer this')}
                      </button>
                    </div>
                  )}
                  {answerControl(s.n)}
                </div>
              </li>
            ))}
          </ol>
          <div className={answering === 'holding' ? 'ring-2 ring-orange-300 rounded-2xl p-1 -m-1' : ''}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Answer lines 3 and 5 here, then confirm the story</div>
            <HoldingEditor holding={h} kinds={kinds} onSaved={(x) => { setAnswering(null); onHoldingSaved(x); }} />
          </div>
          {(into.length > 0 || outof.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Line 1 · what arrives ({into.length})</div>
                <div className="space-y-1.5">{into.map((f) => <FlowRow key={f.id} f={f} dir="in" />)}{!into.length && <p className="text-xs text-slate-400">Nothing recorded yet.</p>}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Line 4 · where it goes ({outof.length})</div>
                <div className="space-y-1.5">{outof.map((f) => <FlowRow key={f.id} f={f} dir="out" />)}{!outof.length && <p className="text-xs text-slate-400">Nothing recorded yet.</p>}</div>
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-400">Prefer the owner's own words? The questionnaire and interview under Questionnaires and interviews fill these lines with their name on the record.</p>
        </div>
      )}
    </div>
  );
}

export default function MapView({ refreshOverview, openIncident }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [openDept, setOpenDept] = useState(null);
  const [openFlowId, setOpenFlowId] = useState(null);
  const [showPicture, setShowPicture] = useState(false);

  const load = useCallback(async () => { const d = await api('/map'); if (d.success) setData(d); }, []);
  useEffect(() => { load(); }, [load]);

  const holdings = data?.holdings || [];
  const flows = data?.flows || [];
  const live = flows.filter((f) => f.state !== 'rejected');
  const kinds = data?.information_kinds || [];
  const basisLabels = data?.basis_labels || {};

  const onSaved = async () => { setOpenFlowId(null); await load(); refreshOverview?.(); };
  const onHoldingSaved = async () => { await load(); refreshOverview?.(); };
  const openFlowInDept = (id) => {
    const f = flows.find((x) => x.id === id); if (!f) return;
    const h = holdings.find((x) => x.node_id === f.from_node_id) || holdings.find((x) => x.node_id === f.to_node_id);
    setShowPicture(false); if (h) setOpenDept(h.id); setOpenFlowId(id);
  };

  const shown = holdings.filter((h) => {
    if (!q.trim()) return true;
    const n = q.toLowerCase();
    const mine = live.filter((f) => f.from_node_id === h.node_id || f.to_node_id === h.node_id);
    return `${h.department_name} ${h.owners || ''} ${(Array.isArray(h.information) ? h.information : []).join(' ')} ${mine.map((f) => `${f.what} ${f.from_name} ${f.to_name}`).join(' ')}`.toLowerCase().includes(n);
  });
  const done = holdings.filter((h) => h.state === 'confirmed').length;
  const vendorFiles = live.filter((f) => !holdings.some((h) => h.node_id === f.from_node_id || h.node_id === f.to_node_id));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">The life of member information, department by department</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">Every department tells the same five-line story, in order: what arrives, what it is used for, where it lives and who may see it, where it goes, and how long it is kept. The owner answers, you confirm. When every story is green, this is the organization's data life cycle.</p>
        </div>
        <DescribeFlow nodes={data?.nodes || []} kinds={kinds} basisLabels={basisLabels} onCreated={onSaved} />
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a department, an owner, or what moves…" className="text-sm border border-slate-300 rounded-lg px-3 py-2 text-slate-700 min-w-[280px]" />
        <span className="text-xs text-slate-500">{done} of {holdings.length} stories confirmed</span>
        <button type="button" onClick={() => setShowPicture(!showPicture)} className={`ml-auto px-3 py-1.5 rounded-lg text-xs font-semibold ${showPicture ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{showPicture ? 'Hide the picture' : 'See the whole picture'}</button>
      </div>

      {showPicture && <Picture nodes={data?.nodes || []} flows={live} onPick={openFlowInDept} />}

      {!data && <p className="text-sm text-slate-400">Loading…</p>}
      {data && !holdings.length && (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-500 text-sm">No departments yet. Go to Program and press “Refresh from what we know”.</div>
      )}
      {shown.map((h) => (
        <DeptStory key={h.id} h={h}
          into={live.filter((f) => f.to_node_id === h.node_id)}
          outof={live.filter((f) => f.from_node_id === h.node_id)}
          nodes={data.nodes || []}
          reload={load}
          kinds={kinds} basisLabels={basisLabels}
          open={openDept === h.id} onToggle={() => setOpenDept(openDept === h.id ? null : h.id)}
          openFlowId={openFlowId} setOpenFlowId={setOpenFlowId}
          onSaved={onSaved} onHoldingSaved={onHoldingSaved}
          links={data?.incident_links || []} openIncident={openIncident} />
      ))}
      {data && holdings.length > 0 && !shown.length && <p className="text-sm text-slate-400 text-center py-4">Nothing matches this search.</p>}

      {vendorFiles.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Files received from vendors ({vendorFiles.length})</div>
          <div className="space-y-1.5">
            {vendorFiles.map((f) => (
              <div key={f.id}>
                <button type="button" onClick={() => setOpenFlowId(openFlowId === f.id ? null : f.id)} className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 border border-slate-100">
                  <span className="text-[12.5px] text-slate-700 flex-1 min-w-0 truncate">{f.what} <span className="text-slate-400">· {f.from_name} → {f.to_name}</span></span>
                  <Pill cfg={STATE[f.state]?.cls}>{STATE[f.state]?.label}</Pill>
                </button>
                {openFlowId === f.id && <div className="mt-2 space-y-2"><SourceLine flow={f} /><FlowEditor flow={f} kinds={kinds} basisLabels={basisLabels} onSaved={onSaved} onClose={() => setOpenFlowId(null)} /></div>}
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-400">Built to NIST Privacy Framework ID.IM-P (inventory and mapping) with HIPAA minimum necessary (45 CFR 164.514(d)) and Medicare Advantage retention (42 CFR 422.504(d)). What vendors do under their contracts lives under Vendors and business associates.</p>
    </div>
  );
}

/** The picture: parties on the left, departments in the middle, vendors and systems on the right. */
function Picture({ nodes, flows, onPick }) {
  const cols = [
    { title: 'Where it comes from', items: nodes.filter((n) => n.kind === 'external') },
    { title: 'Inside the plan', items: nodes.filter((n) => n.kind === 'department') },
    { title: 'Vendors and systems', items: nodes.filter((n) => n.kind === 'vendor' || n.kind === 'system') },
  ];
  const W = 230, H = 34, GAP = 14, X = [20, 380, 740];
  const pos = {};
  cols.forEach((c, ci) => c.items.forEach((n, i) => { pos[n.id] = { x: X[ci], y: 44 + i * (H + GAP) }; }));
  const rows = Math.max(...cols.map((c) => c.items.length), 1);
  const height = 60 + rows * (H + GAP);
  const color = (f) => (f.state === 'confirmed' ? '#10b981' : '#d97706');
  const path = (f) => {
    const a = pos[f.from_node_id], b = pos[f.to_node_id]; if (!a || !b) return null;
    let ax = a.x + W, ay = a.y + H / 2, bx = b.x, by = b.y + H / 2;
    if (a.x > b.x) { ax = a.x; bx = b.x + W; }
    if (a.x === b.x) { ax = a.x + W; bx = b.x + W; }
    const dx = Math.max(60, Math.abs(bx - ax) * 0.45);
    return `M${ax},${ay} C${ax + (ax <= bx ? dx : -dx)},${ay} ${bx + (ax <= bx ? -dx : dx)},${by} ${bx},${by}`;
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex gap-4 text-[11.5px] text-slate-500 mb-2"><span><span className="inline-block w-4 h-[3px] bg-emerald-500 align-middle mr-1.5" />Confirmed</span><span><span className="inline-block w-4 h-[3px] bg-amber-600 align-middle mr-1.5" />Waiting for you</span><span className="ml-auto">Click a line to open it in its department</span></div>
      <div className="overflow-x-auto">
        <svg viewBox={`0 0 990 ${height}`} width="100%" style={{ minWidth: 900, height }} role="img" aria-label="Map of member information">
          {cols.map((c, ci) => <text key={c.title} x={X[ci]} y={24} fontSize="11" fontWeight="800" fill="#64748b" letterSpacing=".08em">{c.title.toUpperCase()}</text>)}
          {flows.map((f) => { const d = path(f); if (!d) return null; return (
            <g key={f.id} style={{ cursor: 'pointer' }} onClick={() => onPick(f.id)}>
              <path d={d} fill="none" stroke="transparent" strokeWidth="14" />
              <path d={d} fill="none" stroke={color(f)} strokeWidth={f.state === 'confirmed' ? 2.5 : 2} strokeDasharray={f.state === 'confirmed' ? 'none' : '6 5'} opacity=".85"><title>{f.from_name} → {f.to_name}: {f.what}</title></path>
            </g>
          ); })}
          {nodes.filter((n) => pos[n.id]).map((n) => (
            <g key={n.id}>
              <rect x={pos[n.id].x} y={pos[n.id].y} width={W} height={H} rx="10" fill="#fff" stroke="#e2e8f0" />
              <text x={pos[n.id].x + 12} y={pos[n.id].y + 22} fontSize="12.5" fontWeight="700" fill="#0f172a">{n.name.length > 30 ? n.name.slice(0, 29) + '…' : n.name}</text>
            </g>
          ))}
        </svg>
      </div>
    </div>
  );
}
